import * as React from 'react';
import {FeatureAppDefinition} from '@feature-hub/core';
import {ReactFeatureApp} from '@feature-hub/react';
import {ThemeContext} from 'styled-components';
import {
  requiredExternals,
  requiredServices,
  optionalServices,
  TariffsFeatureApp,
  TariffsFeatureAppDependencies,
  TariffsFeatureAppConfig,
} from '@volkswagen-onehub/whitelabel-e-mobility-tariffs-feature-app';
import {
  CommonFeatureServices,
  TariffsFeatureAppComponentLibrary,
  TariffsFeatureAppContentConfig,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {
  Button,
  ButtonInverted,
} from '@volkswagen-onehub/vw-e-mobility-components/src/button';
import {
  Text,
  TextInverted,
} from '@volkswagen-onehub/vw-e-mobility-components/src/text';
import {Icon} from '@volkswagen-onehub/vw-e-mobility-components/src/icon';
import {Accordion} from '@volkswagen-onehub/vw-e-mobility-components/src/accordion';
import {
  customOptionalVwServices,
  optionalVwServices,
  RequiredVwFeatureServices,
  requiredVwServices,
  VwFeatureServiceIds,
  devConfig,
  getVwTrack,
  fetchTariffsBffConfig,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  createTariffsFeatureAppVariantTheme,
  TariffsFeatureAppWrapper,
} from './tariffs-theme';
import {
  Gallery,
  GallerySlide,
} from '@volkswagen-onehub/vw-e-mobility-components/src/gallery';
import {BrandThemeProvider} from '@volkswagen-onehub/vw-e-mobility-components';
import {Frame} from '@volkswagen-onehub/vw-e-mobility-components/src/frame';
import {Link} from '@volkswagen-onehub/vw-e-mobility-components/src/link';
import {Flag} from '@volkswagen-onehub/vw-e-mobility-components/src/flag';
import {Divider} from '@volkswagen-onehub/vw-e-mobility-components/src/divider';
import {RecommendationBox} from '@volkswagen-onehub/vw-e-mobility-components/src/recommendation-box';
import {getTariffsTrackingFunctions} from './tariffs-tracking';
import {ApplicationRoot} from '@volkswagen-onehub/components-core';

const tariffsComponentsLibrary: TariffsFeatureAppComponentLibrary = {
  Accordion,
  Button,
  ButtonInverted,
  Divider,
  Flag,
  Frame,
  Gallery,
  GallerySlide,
  Icon,
  Link,
  RecommendationBox,
  Text,
  TextInverted,
};

const tariffsFeatureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  TariffsFeatureAppDependencies & RequiredVwFeatureServices,
  TariffsFeatureAppConfig
> = {
  dependencies: {
    externals: requiredExternals,
    featureServices: {
      ...requiredVwServices,
      ...requiredServices,
    },
  },
  optionalDependencies: {
    featureServices: {
      ...optionalVwServices,
      ...optionalServices,
      ...customOptionalVwServices,
    },
  },
  create: function (env): ReactFeatureApp {
    const localeService =
      env.featureServices[CommonFeatureServices.localeService];

    const appDataContent = env.featureServices[
      VwFeatureServiceIds.contentService
    ].content as TariffsFeatureAppContentConfig;

    const appDataPromise = fetchTariffsBffConfig(
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl
    );

    const vwTrack = getVwTrack(
      env.featureServices[VwFeatureServiceIds.trackingManager],
      localeService
    );

    const trackingFunctions = getTariffsTrackingFunctions(vwTrack);
    const loadingPromise = appDataPromise.then(() => undefined);

    return {
      loadingPromise,
      render() {
        return (
          <ApplicationRoot>
            <BrandThemeProvider theme="main">
              <ThemeContext.Consumer>
                {(brandTheme) => (
                  <TariffsFeatureAppWrapper>
                    <TariffsFeatureApp
                      componentsLibrary={tariffsComponentsLibrary}
                      theme={createTariffsFeatureAppVariantTheme(brandTheme)}
                      featureAppEnvironment={env}
                      appDataContent={appDataContent}
                      appDataPromise={appDataPromise}
                      trackingFunctions={trackingFunctions}
                      devConfig={devConfig}
                    />
                  </TariffsFeatureAppWrapper>
                )}
              </ThemeContext.Consumer>
            </BrandThemeProvider>
          </ApplicationRoot>
        );
      },
    };
  },
};

export default tariffsFeatureAppDefinition;
